import { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useJourneyStatus } from "@/hooks/useJourneyStatus";
import { useUserRoles } from "@/hooks/useUserRoles";
import { useToast } from "@/hooks/use-toast";
import { useEffect, useRef } from "react";
import MainLayout from "@/components/layout/MainLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

interface JourneyRouteGuardProps {
  children: ReactNode;
}

// Routes that require journey completion
const PROTECTED_ROUTES = [
  '/una-governance',
  '/workflows',
  '/governance-documents',
  '/una-members',
];

export function JourneyRouteGuard({ children }: JourneyRouteGuardProps) {
  const { user } = useAuth();
  const location = useLocation();
  const { journeyCompleted, isLoading } = useJourneyStatus(user?.id);
  const { bypassMemberOnboarding, isReviewer, isLoading: rolesLoading } = useUserRoles(user?.id);
  const { toast } = useToast();
  const hasShownToast = useRef(false);

  const isProtectedRoute = PROTECTED_ROUTES.some(route =>
    location.pathname.startsWith(route)
  );

  useEffect(() => {
    if (
      !isLoading &&
      !rolesLoading &&
      isProtectedRoute &&
      !journeyCompleted &&
      !bypassMemberOnboarding &&
      !hasShownToast.current
    ) {
      hasShownToast.current = true;
      toast({
        title: "Complete Your Journey First",
        description: "Please complete your onboarding journey to access this section.",
        variant: "default",
      });
    }
  }, [isLoading, rolesLoading, isProtectedRoute, journeyCompleted, bypassMemberOnboarding, toast]);

  // Reset toast flag when leaving protected route
  useEffect(() => {
    if (!isProtectedRoute) {
      hasShownToast.current = false;
    }
  }, [isProtectedRoute]);

  if (isLoading || rolesLoading) {
    return null;
  }

  if (isProtectedRoute && !journeyCompleted) {
    // Reviewer / admin / support accounts don't have a member journey or UNA.
    // Show a plain notice instead of pushing them into the onboarding wizard.
    if (bypassMemberOnboarding) {
      return (
        <MainLayout>
          <div className="container max-w-2xl mx-auto py-16">
            <Card>
              <CardHeader>
                <CardTitle>Reviewer account</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm text-muted-foreground">
                <p>
                  This section is part of the member portal and requires an
                  association (UNA). Your account is a{" "}
                  {isReviewer ? "reviewer" : "staff"} account and does not have one.
                </p>
                <p>
                  You can continue to the{" "}
                  {isReviewer ? (
                    <a href="/review" className="underline">Reviewer Workbench</a>
                  ) : (
                    <a href="/support" className="underline">Support library</a>
                  )}
                  .
                </p>
              </CardContent>
            </Card>
          </div>
        </MainLayout>
      );
    }
    return <Navigate to="/journey" replace />;
  }

  return <>{children}</>;
}
